import React, { useContext, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { BidAdjustment } from '@bingads-webui-campaign/target-bid-adjustment';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { modelToViewModelForLocationTargets } from '@bingads-webui-campaign-react/xandr-campaign';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getBidAdjustmentStylesTargetStyles } from './styles/bid-adjustments-styles';

const criterionTypeToString = {
  JobFunction: _TL_('Job Function'),
  Industry: _TL_('Industry'),
  CompanyName: _TL_('Company'),
  Age: _TL_('Age'),
  Gender: _TL_('Gender'),
  Device: _TL_('Device'),
  Location: _TL_('Location'),
  Audience: _TL_('Audience'),
};

const typeToValidationKey = {
  JobFunction: ['jobTargets', 'negativeJobTargets'],
  Industry: ['industry', 'negativeIndustry'],
  CompanyName: ['companyNameTargets', 'negativeCompanyNameTargets'],
  Age: ['ageRanges'],
  Gender: ['genderTypes'],
  Audience: ['audiences'],
  Location: ['locationTargets'],
};

const criterionsToFetch = [
  'AgeCriterion',
  'GenderCriterion',
  'CompanyNameCriterion',
  'IndustryCriterion',
  'JobFunctionCriterion',
  'LocationCriterion',
  'AudienceCriterion',
];

const BidAdjustmentInternal = ({
  cid,
  data,
  aid,
  campaignId,
  adGroupId,
  updateValidation,
}) => {
  const [bidAdjustmentRef, setBidAdjustmentRef] = useState();

  const { bundleCountries } = data;

  const activity = useContext(ActivityContext);

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getBidAdjustmentStylesTargetStyles);

  const currentLevelTargetInfo = useMemo(() => (new AudienceTargetInfo({
    resourceName: 'AdGroupCriterion',
    isForVNext: true,
    initAudienceAssociationView: true,
  })), []);

  const onChange = useCallback((changedKey) => {
    const updatedItems = currentLevelTargetInfo.getSnapshot(false);

    const targets = updatedItems.filter(({ CriterionType }) => (CriterionType === changedKey))
      .map(({ Id, Status, Criterion }) => ({ Id, status: Status, Criterion }));

    const negativeTargets = updatedItems.filter(({ CriterionType }) => (CriterionType === `Negative${changedKey}`))
      .map(({ Id, Status, Criterion }) => ({ Id, status: Status, Criterion }));

    const validationKeys = typeToValidationKey[changedKey];
    if (targets.length > 0 && negativeTargets.length > 0) {
      updateValidation([], { [validationKeys[0]]: [...targets], [validationKeys[1]]: [...negativeTargets] });
    } else if (negativeTargets.length > 0) {
      updateValidation([], { [validationKeys[1]]: [...negativeTargets] });
    } else if (changedKey === 'Audience') {
      const audiences = currentLevelTargetInfo.criterionsByType.Audience;
      updateValidation([], { audiences: [...audiences] });
    } else if (changedKey === 'Location') {
      const adjustedLocations = currentLevelTargetInfo.criterionsByType.Location;

      const modelForParsing = _.map(adjustedLocations, location => ({
        ...location,
        CriterionBid: {
          Multiplier: location.Criterion.BidAdjustment,
        },
      }));

      const location = modelToViewModelForLocationTargets({
        model: modelForParsing, adGroupId, bundleCountries, overrideOriginalBidAdjustment: true,
      });

      updateValidation([], { locationTargets: location.locationTargets });
    } else {
      updateValidation([], { [validationKeys[0]]: [...targets] });
    }

    if (activity) {
      activity.signal('onBidAdjustmentChanged');
    }
  }, [activity, adGroupId, bundleCountries, currentLevelTargetInfo, updateValidation]);

  useEffect(() => {
    if (bidAdjustmentRef) {
      bidAdjustmentRef.innerHTML = '';

      Promise.all([
        currentLevelTargetInfo.fetchCriterions(campaignId, adGroupId, criterionsToFetch),
        currentLevelTargetInfo.initializeWithhAdGroupAudience(campaignId, adGroupId),
      ]).then(() => {
        const allKeys = currentLevelTargetInfo.getAllNoneEmptyTypeKeys();

        _.each(allKeys, (key) => {
          currentLevelTargetInfo.off(`AdGroupCriterion${key}Changed`, () => onChange(key));
          const view = new BidAdjustment({
            currentLevelTargetInfo,
            isForVNext: true,
            campaignId,
            adGroupId,
            criterionTypes: [key],
            supportCashbackSettings: false,
            showExclusion: false,
          });

          view.render();

          const header = document.createElement('h5');
          header.innerHTML = i18n.getString(criterionTypeToString[key]);
          header.style.cssText = 'margin-top: 20px;';

          bidAdjustmentRef.append(header);
          bidAdjustmentRef.append(view.el);

          onChange(key);
          currentLevelTargetInfo.on(`AdGroupCriterion${key}Changed`, () => onChange(key));
        });
      });
    }
  }, [aid, bidAdjustmentRef, campaignId, adGroupId, cid, currentLevelTargetInfo, i18n, onChange]);

  return (
    <div className={classNames('job-function-picker', classes.pickerContainer)} >
      <div className="criterion-setting-container">
        <div className="target-edit-container">
          <div className={classNames('job-function-association-view', 'criterion-setting-JobFunction')} ref={newRef => setBidAdjustmentRef(newRef)} />
        </div>
      </div>
    </div>
  );
};

BidAdjustmentInternal.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  adGroupId: PropTypes.string.isRequired,
  data: PropTypes.shape({
    bundleCountries: PropTypes.arrayOf(PropTypes.shape([])),
  }).isRequired,
};

export const ValidatedBidAdjustment = withCustomValidation(BidAdjustmentInternal);
